import {differenceInHours} from 'date-fns';

import {isNil, isNotNil} from 'ramda';

import {useGetRentalPriceGroupQuery, useGetRentalVehicleQuery} from '@dms/api/vehicle';
import {useTenant, useVatRatesOptions} from '@dms/shared';

import {Nullish} from 'shared/web';

import {calculateCommonPrice} from '../../../../RentalOrderDetail/utils/calculateCommonPrice';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';

type Money = {amount: number; currency: string};

/**
 * States for which the rental order detail deliberately shows no expected price,
 * mirroring `useRentalOrderTotalPrice`.
 */
const STATES_WITHOUT_EXPECTED_PRICE = ['draft', 'canceled'];

/**
 * The VAT type `calculateCommonPrice` applies. Kept in sync with its own `DEFAULT_VAT_TYPE`
 * so the guard below can tell in advance whether that rate exists.
 */
const STANDARD_VAT_TYPE = 'S';

interface RentalEventPricing {
  dailyRate: Money | Nullish;
  expectedPrice: Money | Nullish;
  expectedPriceWithVat: Money | Nullish;
  /** True only while the price group fallback is being fetched, never for grid-provided prices. */
  isLoading: boolean;
}

/**
 * Pricing shown in the calendar hover tooltip.
 *
 * The `rental-order` data grid derives every money column from the order's own rent price
 * (`rentVehicle`/`returnVehicle`), which the backend only creates once the vehicle is actually
 * handed over. Orders in `draft` or `reserved` therefore come back with empty money cells even
 * though a rate has been configured, which is what the order detail shows instead: the rate of
 * the rental vehicle's price group.
 *
 * This hook applies that same fallback so the tooltip and the detail agree (T20-138188). Values
 * the grid does provide always win, so an agreed rate is never overwritten by the list price.
 */
export function useRentalEventPricing(
  event: RentalResourceCalendarEvent | Nullish
): RentalEventPricing {
  const {tenantCurrency} = useTenant();
  const [vatRates] = useVatRatesOptions();

  const rentalOrder = event?.rentalOrder;
  const vehicleId = rentalOrder?.vehicleId;

  const isFallbackNeeded =
    isNil(rentalOrder?.dailyRate) ||
    isNil(rentalOrder?.expectedPrice) ||
    isNil(rentalOrder?.expectedPriceWithVat);

  const {data: rentalVehicle, isLoading: isRentalVehicleLoading} = useGetRentalVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNil(vehicleId) || !isFallbackNeeded}
  );

  const rentalPriceGroupId = rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId;

  const {data: priceGroup, isError: isPriceGroupError} = useGetRentalPriceGroupQuery(
    {rentalPriceGroupId: rentalPriceGroupId ?? ''},
    {skip: isNil(rentalPriceGroupId)}
  );

  // Derived from the data rather than the price group's own `isLoading`, which is still false on
  // the render where the vehicle has just arrived and its query has not been dispatched yet.
  const isPriceGroupPending =
    isNotNil(rentalPriceGroupId) && isNil(priceGroup) && !isPriceGroupError;

  const isLoading = isFallbackNeeded && (isRentalVehicleLoading || isPriceGroupPending);

  const priceGroupItem = priceGroup?.items?.find(
    (item) =>
      item.rentalPriceGroupItemId === rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId
  );

  const listDailyRate = toAmount(priceGroupItem?.price?.rate);
  const paidDays = getPaidDays(event);

  const isExpectedPriceHidden = STATES_WITHOUT_EXPECTED_PRICE.includes(rentalOrder?.state ?? '');

  const listExpectedPrice =
    isNotNil(listDailyRate) && isNotNil(paidDays) && !isExpectedPriceHidden
      ? listDailyRate * paidDays
      : null;

  // `calculateCommonPrice` is what both the order detail header and the rent form use to price a
  // not-yet-rented order, so the tooltip has to apply VAT the same way to arrive at the same
  // number. It throws when the standard rate is missing, which must never take down the calendar.
  const hasStandardVatRate = vatRates.some((rate) => rate.value === STANDARD_VAT_TYPE);

  const listExpectedPriceWithVat =
    isNotNil(listExpectedPrice) && hasStandardVatRate
      ? calculateCommonPrice(listExpectedPrice, vatRates).withVat
      : null;

  return {
    dailyRate: rentalOrder?.dailyRate ?? toMoney(listDailyRate, tenantCurrency),
    expectedPrice: rentalOrder?.expectedPrice ?? toMoney(listExpectedPrice, tenantCurrency),
    expectedPriceWithVat:
      rentalOrder?.expectedPriceWithVat ?? toMoney(listExpectedPriceWithVat, tenantCurrency),
    isLoading,
  };
}

/**
 * Days billed for the rental, counted the way `calculateRentPrice` does on the order detail:
 * every started day is charged. Deliberately derived from the event's own dates rather than the
 * grid's `rentalLength`, whose backend definition counts whole calendar days and would price a
 * partial last day differently from the detail page.
 */
const getPaidDays = (event: RentalResourceCalendarEvent | Nullish) => {
  if (isNil(event?.start) || isNil(event?.end)) {
    return null;
  }

  const paidDays = Math.ceil(differenceInHours(event.end, event.start) / 24);

  return paidDays > 0 ? paidDays : null;
};

const toAmount = (rate: string | Nullish) => {
  if (isNil(rate)) {
    return null;
  }

  const amount = parseFloat(rate);

  return isNaN(amount) ? null : amount;
};

const toMoney = (amount: number | Nullish, currency: string | Nullish): Money | Nullish => {
  if (isNil(amount) || isNil(currency)) {
    return null;
  }

  return {amount, currency};
};
